// ----------------------------------------------------------------------------
// Snippet: Video Player
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Vimeo from '@vimeo/player';

// Styles
import './_video-block__controls.scss';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
var VIDEO_BLOCK = '.video-block';
var VIDEO = '.video-block__video';
var YOUTUBE_VIDEO = '.video-block--youtube';
var VIMEO_VIDEO = '.video-block--vimeo';
var HTML5_VIDEO = '.video-block--html5';
var CONTROLS_PLAY = '.video-block__control--play';
var CONTROLS_PAUSE = '.video-block__control--pause';
var CONTROLS_MEDIA_TOGGLE = '.video-block__control--media-toggle';

// When controls live outside `.video-block` (e.g. banner), mirror pause state for icon swap CSS.
var TOGGLE_HOST_PAUSED_CLASS = 'video-block-controls-host--paused';

// Classes
var POSTERED_CLASS = 'video-block--is-postered';
var PAUSED_CLASS = 'video-block--is-paused';
var DYNAMIC_VIDEO_CLASS = 'video-block--dynamic-video';

// Events
var playerPlay = new Event('onplay.video-block');
var playerPause = new Event('onpause.video-block');
var play = new Event('play.video-block');
var pause = new Event('pause.video-block');
function syncToggleControlsHost(videoBlock) {
  var host = videoBlock.toggleControlsHost;
  if (!host) {
    return;
  }
  if (videoBlock.classList.contains(PAUSED_CLASS)) {
    host.classList.add(TOGGLE_HOST_PAUSED_CLASS);
  } else {
    host.classList.remove(TOGGLE_HOST_PAUSED_CLASS);
  }
}
function syncPlayPauseButton(videoBlock) {
  var btn = videoBlock.mediaToggleButton;
  if (!btn) {
    return;
  }
  var isPaused = videoBlock.classList.contains(PAUSED_CLASS);
  var labelPlay = btn.dataset.labelPlay;
  var labelPause = btn.dataset.labelPause;
  btn.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
  if (labelPlay && labelPause) {
    btn.setAttribute('aria-label', isPaused ? labelPlay : labelPause);
  }
  syncToggleControlsHost(videoBlock);
}

// -------------------------------------
// Helpers
// -------------------------------------

var videoBlocks = {
  init: function init() {
    mountVideoBlocks(document);
  },
  initVideoBlock: function initVideoBlock(videoBlock) {
    var videoEl = videoBlock.querySelector(VIDEO);
    if (!videoEl) {
      return;
    }
    var extControlsId = videoBlock.dataset.externalControls;
    var extHost = extControlsId ? document.getElementById(extControlsId) : null;
    var controlsRoot = extHost || videoBlock;
    videoBlock.toggleControlsHost = extHost || null;
    videoBlock.mediaToggleButton = controlsRoot.querySelector(CONTROLS_MEDIA_TOGGLE);
    videoBlock.playButton = controlsRoot.querySelector(CONTROLS_PLAY);
    videoBlock.pauseButton = controlsRoot.querySelector(CONTROLS_PAUSE);
    videoEl.setAttribute('inert', '');
    syncPlayPauseButton(videoBlock);
    videoBlock.addEventListener('onplay.video-block', function () {
      videoBlockControls.play(videoBlock);
    });
    videoBlock.addEventListener('onpause.video-block', function () {
      videoBlockControls.pause(videoBlock);
    });
    if (videoBlock.mediaToggleButton) {
      videoBlock.mediaToggleButton.addEventListener('click', function () {
        if (videoBlock.classList.contains(PAUSED_CLASS)) {
          runHtml5PlayFromControls(videoBlock, videoEl);
        } else {
          runHtml5PauseFromControls(videoBlock, videoEl);
        }
      });
    } else {
      if (videoBlock.playButton !== null) {
        videoBlock.playButton.addEventListener('click', function () {
          var _videoBlock$pauseButt;
          runHtml5PlayFromControls(videoBlock, videoEl);
          (_videoBlock$pauseButt = videoBlock.pauseButton) === null || _videoBlock$pauseButt === void 0 || _videoBlock$pauseButt.focus();
        });
      }
      if (videoBlock.pauseButton !== null) {
        videoBlock.pauseButton.addEventListener('click', function () {
          var _videoBlock$playButto;
          runHtml5PauseFromControls(videoBlock, videoEl);
          (_videoBlock$playButto = videoBlock.playButton) === null || _videoBlock$playButto === void 0 || _videoBlock$playButto.focus();
        });
      }
    }
  }
};
var videoBlockControls = {
  play: function play(videoBlock) {
    videoBlock.classList.remove(POSTERED_CLASS);
    videoBlock.classList.remove(PAUSED_CLASS);
    syncPlayPauseButton(videoBlock);
  },
  pause: function pause(videoBlock) {
    videoBlock.classList.add(PAUSED_CLASS);
    syncPlayPauseButton(videoBlock);
  }
};
function runHtml5PlayFromControls(videoBlock, videoEl) {
  videoBlockControls.play(videoBlock);
  videoBlock.dispatchEvent(play);
  videoEl.removeAttribute('inert');
}
function runHtml5PauseFromControls(videoBlock, videoEl) {
  videoBlockControls.pause(videoBlock);
  videoBlock.dispatchEvent(pause);
  videoEl.setAttribute('inert', '');
}
var youtubeVideos = {
  init: function init() {
    var tag = document.createElement('script');
    var firstScriptTag = document.getElementsByTagName('script')[0];
    tag.src = 'https://www.youtube.com/iframe_api';
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    window.onYouTubeIframeAPIReady = function () {
      youtubeVideos.initVideos();
    };
  },
  initVideos: function initVideos() {
    var videoBlocks = document.querySelectorAll(YOUTUBE_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = youtubeVideos.getVideo(videoBlock);
      var videoIframe = document.createElement('div');
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      video.appendChild(videoIframe);
      new window.YT.Player(videoIframe, {
        videoId: videoID,
        playerVars: {
          playlist: videoSettings.playlist_id == null && videoSettings.is_looped ? videoID : null,
          loop: videoSettings.is_looped ? 1 : 0,
          autoplay: videoSettings.is_autoplay ? 1 : 0,
          controls: videoSettings.is_controls ? 1 : 0,
          disablekb: videoSettings.is_controls ? 0 : 1,
          playsinline: videoSettings.is_autoplay ? 1 : 0,
          showinfo: 0,
          modestbranding: 1,
          fs: 1,
          cc_load_policy: 0,
          iv_load_policy: 3,
          rel: 0
        },
        events: {
          'onReady': youtubeVideos.onPlayerReady
        }
      });
    });
  },
  onPlayerReady: function onPlayerReady(event) {
    var player = event.target;
    var video = youtubeVideos.getVideo(player.getIframe().closest(VIDEO_BLOCK));
    var videoBlock = video.closest(VIDEO_BLOCK);
    var videoSettings;
    try {
      videoSettings = JSON.parse(video.dataset.videoSettings);
    } catch (_unused) {
      return;
    }
    player.setVolume(videoSettings.is_muted ? 0 : 100);
    videoBlock.addEventListener('play.video-block', function () {
      youtubeVideos.play(player);
    });
    videoBlock.addEventListener('pause.video-block', function () {
      youtubeVideos.pause(player);
    });
    player.getIframe().tabIndex = -1;
  },
  play: function play(player) {
    var iframe = player.getIframe();
    iframe.tabIndex = 0;
    player.playVideo();
  },
  pause: function pause(player) {
    player.pauseVideo();
  },
  getVideo: function getVideo(videoBlock) {
    return videoBlock.querySelector(VIDEO);
  }
};
var vimeoVideos = {
  init: function init() {
    var videoBlocks = document.querySelectorAll(VIMEO_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = videoBlock.querySelector(VIDEO);
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      if (videoSettings.lookup_poster) {
        var url = 'https://vimeo.com/api/v2/video/' + videoID + '.json';
        fetch(url).then(response => response.json()).then(data => {
          videoBlock.classList.add(POSTERED_CLASS);
          videoBlock.style.backgroundImage = 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(' + data[0].thumbnail_large + ')';
        }).catch(() => {
          // error can be passed in as a parameter to be used
          videoBlock.classList.remove(POSTERED_CLASS);
          videoBlock.style.backgroundImage = '';
        });
      }
      var player = new Vimeo(video, {
        id: videoID,
        loop: videoSettings.is_looped,
        autoplay: videoSettings.is_autoplay,
        autopause: !videoSettings.is_autoplay,
        title: false,
        portrait: false,
        byline: false,
        controls: videoSettings.is_controls
      });
      player.setVolume(videoSettings.is_muted ? 0 : 1);
      videoBlock.addEventListener('play.video-block', function () {
        vimeoVideos.play(player);
      });
      videoBlock.addEventListener('pause.video-block', function () {
        vimeoVideos.pause(player);
      });
    });
  },
  play: function play(player) {
    player.play();
  },
  pause: function pause(player) {
    player.pause();
  }
};
function setupHtml5AutoplayWhenInView(videoBlock, videoEl) {
  if (videoBlock.dataset.autoplayInView !== 'true') {
    return;
  }
  var runPlay = function runPlay() {
    runHtml5PlayFromControls(videoBlock, videoEl);
  };
  var runPause = function runPause() {
    runHtml5PauseFromControls(videoBlock, videoEl);
  };
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        runPlay();
      } else {
        runPause();
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0
  });
  observer.observe(videoBlock);
}
function setupPauseOnContainerClick(videoBlock, videoEl) {
  if (videoBlock.dataset.videoPauseOnClick !== 'true') {
    return;
  }
  if (videoBlock.dataset.pauseOnClickMounted === '1') {
    return;
  }
  videoBlock.dataset.pauseOnClickMounted = '1';
  videoBlock.addEventListener('click', function (event) {
    if (event.target.closest('.video-block__control')) {
      return;
    }
    if (!videoEl.paused) {
      videoEl.pause();
    }
  });
}
function mountHtml5VideoBlocks(root) {
  if (!root || !root.querySelectorAll) {
    return;
  }
  root.querySelectorAll(HTML5_VIDEO).forEach(function (videoBlock) {
    if (videoBlock.dataset.h5VideoMounted === '1') {
      return;
    }
    videoBlock.dataset.h5VideoMounted = '1';
    var video = videoBlock.querySelector(VIDEO);
    if (!video) {
      return;
    }
    if (videoBlock.classList.contains(DYNAMIC_VIDEO_CLASS)) {
      var videoSources = JSON.parse(video.dataset.videoSources);
      var desktopVideoUrls = videoSources.desktop_video_source_urls.split(',');
      var desktopVideoSourceTypes = videoSources.desktop_video_source_types.split(',');
      var mobileVideoUrls = videoSources.mobile_video_source_urls.split(',');
      var mobileVideoSourceTypes = videoSources.mobile_video_source_types.split(',');
      var sourceUrls;
      var sourceTypes;
      if (window.innerWidth >= 1024 || mobileVideoUrls[0] == '') {
        sourceUrls = desktopVideoUrls;
        sourceTypes = desktopVideoSourceTypes;
      } else {
        sourceUrls = mobileVideoUrls;
        sourceTypes = mobileVideoSourceTypes;
      }
      for (var index = 0; index < sourceUrls.length; index++) {
        if (sourceUrls[index]) {
          video.insertAdjacentHTML('beforeend', "<source type='".concat(sourceTypes[index], "' src='").concat(sourceUrls[index], "'/>"));
        }
      }
    }
    videoBlock.addEventListener('play.video-block', function () {
      video.play();
    });
    videoBlock.addEventListener('pause.video-block', function () {
      video.pause();
    });
    video.onplay = function () {
      videoBlock.dispatchEvent(playerPlay);
    };
    video.onpause = function () {
      videoBlock.dispatchEvent(playerPause);
    };
    setupHtml5AutoplayWhenInView(videoBlock, video);
    setupPauseOnContainerClick(videoBlock, video);
  });
}
export function mountVideoBlocks(root) {
  if (!root || !root.querySelectorAll) {
    return;
  }
  root.querySelectorAll(VIDEO_BLOCK).forEach(function (videoBlock) {
    if (videoBlock.dataset.vbUiMounted === '1') {
      return;
    }
    videoBlock.dataset.vbUiMounted = '1';
    videoBlocks.initVideoBlock(videoBlock);
  });
  mountHtml5VideoBlocks(root);
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  mountVideoBlocks(document);
  youtubeVideos.init();
  vimeoVideos.init();
});